import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import {
 CalendarDays,
 ChevronRight,
 CircleDot,
 MapPin,
 Share2,
 Shield,
 Target,
 Trophy,
 Users,
} from "lucide-react";
import "./RacquetTeamPublic.css";

function RacquetTeamPublic({
 team,
 playersAttr,
 slug,
 navigate,
 theme,
 bannerSlot,
}) {
 const games = team?.games || [];
 const roster = playersAttr || [];

 const teamName = team?.name || "Team";
 const teamLogo = team?.logo || null;

 const teamPhoto =
  team?.season_team_photo ||
  team?.seasonTeamPhoto ||
  team?.team_photo ||
  team?.photo ||
  null;

 const captainName = useMemo(() => {
  if (typeof team?.captain === "string") {
   return team.captain;
  }

  if (team?.captainName) {
   return team.captainName;
  }

  if (team?.captain?.firstName || team?.captain?.lastName) {
   return `${team.captain.firstName || ""} ${team.captain.lastName || ""
    }`.trim();
  }

  return "—";
 }, [team]);

 const sortedGames = useMemo(() => {
  return [...games].sort((a, b) => {
   return new Date(a.date || 0) - new Date(b.date || 0);
  });
 }, [games]);

 const today = new Date();
 today.setHours(0, 0, 0, 0);

 const hasScores = (game) => {
  return (
   game?.score_team1 !== null &&
   game?.score_team1 !== undefined &&
   game?.score_team2 !== null &&
   game?.score_team2 !== undefined
  );
 };

 const isZeroZero = (game) => {
  return (
   Number(game?.score_team1) === 0 &&
   Number(game?.score_team2) === 0
  );
 };

 const isCompletedGame = (game) => {
  return hasScores(game) && !isZeroZero(game);
 };

 const isUpcomingGame = (game) => {
  if (!game?.date || isCompletedGame(game)) {
   return false;
  }

  const gameDate = new Date(`${game.date}T00:00:00`);
  gameDate.setHours(0, 0, 0, 0);

  return gameDate >= today;
 };

 const completedGames = sortedGames.filter(isCompletedGame);
 const upcomingGames = sortedGames.filter(isUpcomingGame);
 const nextGame = upcomingGames[0] || null;

 const formatDate = (date) => {
  if (!date) return "Date TBD";

  return new Date(`${date}T00:00:00Z`).toLocaleDateString(
   "en-US",
   {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
   }
  );
 };

 const formatTime = (time) => {
  if (!time) return "";

  const [hours, minutes] = time.split(":");

  if (hours === undefined || minutes === undefined) {
   return time;
  }

  const timeDate = new Date();
  timeDate.setHours(Number(hours), Number(minutes), 0, 0);

  return timeDate.toLocaleTimeString("en-US", {
   hour: "numeric",
   minute: "2-digit",
   hour12: true,
  });
 };

 const getOpponentName = (game) => {
  if (!game) return "Opponent TBD";

  const currentTeamName = teamName.trim();
  const team1 = game.team1?.trim();
  const team2 = game.team2?.trim();

  if (team1 === currentTeamName) {
   return team2 || "Opponent TBD";
  }

  if (team2 === currentTeamName) {
   return team1 || "Opponent TBD";
  }

  return team2 || team1 || "Opponent TBD";
 };

 const getTeamScore = (game) => {
  const currentTeamName = teamName.trim();

  if (game.team1?.trim() === currentTeamName) {
   return Number(game.score_team1);
  }

  if (game.team2?.trim() === currentTeamName) {
   return Number(game.score_team2);
  }

  return null;
 };

 const getOpponentScore = (game) => {
  const currentTeamName = teamName.trim();

  if (game.team1?.trim() === currentTeamName) {
   return Number(game.score_team2);
  }

  if (game.team2?.trim() === currentTeamName) {
   return Number(game.score_team1);
  }

  return null;
 };

 const getResultLabel = (game) => {
  if (!isCompletedGame(game)) {
   return "Upcoming";
  }

  const teamScore = getTeamScore(game);
  const opponentScore = getOpponentScore(game);

  if (teamScore === null || opponentScore === null) {
   return "Completed";
  }

  if (teamScore > opponentScore) return "Win";
  if (teamScore < opponentScore) return "Loss";

  return "Tie";
 };

 const formatScore = (game) => {
  if (!isCompletedGame(game)) {
   return "—";
  }

  const teamScore = getTeamScore(game);
  const opponentScore = getOpponentScore(game);

  if (teamScore === null || opponentScore === null) {
   return `${game.score_team1} - ${game.score_team2}`;
  }

  return `${teamScore} - ${opponentScore}`;
 };

 const record = useMemo(() => {
  let wins = 0;
  let losses = 0;
  let ties = 0;

  completedGames.forEach((game) => {
   const result = getResultLabel(game);

   if (result === "Win") wins += 1;
   if (result === "Loss") losses += 1;
   if (result === "Tie") ties += 1;
  });

  if (ties > 0) {
   return `${wins} - ${losses} - ${ties}`;
  }

  return `${wins} - ${losses}`;
 }, [completedGames, teamName]);

 const getInitials = (name) => {
  if (!name) return "T";

  return name
   .split(" ")
   .filter(Boolean)
   .slice(0, 2)
   .map((part) => part.charAt(0).toUpperCase())
   .join("");
 };

 const getPlayerName = (player) => {
  return `${player?.firstName || ""} ${player?.lastName || ""
   }`.trim() || "Player";
 };

 const getPlayerRole = (player) => {
  const playerName = getPlayerName(player).toLowerCase();

  if (
   captainName !== "—" &&
   playerName === captainName.toLowerCase()
  ) {
   return "Captain";
  }

  return player?.role || player?.position || "Player";
 };

 const getPlayerSetsPlayed = (player) => {
  return (
   player?.setsPlayed ??
   player?.sets_played ??
   player?.stats?.setsPlayed ??
   "—"
  );
 };

 const getPlayerSetsWon = (player) => {
  return (
   player?.setsWon ??
   player?.sets_won ??
   player?.stats?.setsWon ??
   "—"
  );
 };

 const getPlayerWinPercentage = (player) => {
  const directPercentage =
   player?.winPercentage ??
   player?.win_percentage ??
   player?.stats?.winPercentage;

  if (
   directPercentage !== null &&
   directPercentage !== undefined
  ) {
   return String(directPercentage).includes("%")
    ? directPercentage
    : `${directPercentage}%`;
  }

  const setsPlayed = Number(getPlayerSetsPlayed(player));
  const setsWon = Number(getPlayerSetsWon(player));

  if (
   Number.isNaN(setsPlayed) ||
   Number.isNaN(setsWon) ||
   setsPlayed === 0
  ) {
   return "—";
  }

  return `${Math.round((setsWon / setsPlayed) * 100)}%`;
 };

 const teamStats = [
  {
   label: "Record",
   value: record,
  },
  {
   label: "Sets Won / Lost",
   value:
    team?.setsWon !== undefined ||
     team?.sets_won !== undefined
     ? `${team?.setsWon ?? team?.sets_won ?? 0
     } - ${team?.setsLost ?? team?.sets_lost ?? 0
     }`
     : "—",
  },
  {
   label: "Games Won / Lost",
   value:
    team?.gamesWon !== undefined ||
     team?.games_won !== undefined
     ? `${team?.gamesWon ?? team?.games_won ?? 0
     } - ${team?.gamesLost ?? team?.games_lost ?? 0
     }`
     : "—",
  },
  {
   label: "Rank",
   value:
    team?.rank !== null && team?.rank !== undefined
     ? `#${team.rank}`
     : "—",
  },
 ];

 const recentResults = [...completedGames]
  .reverse()
  .slice(0, 5);

 const rosterPreview = roster.slice(0, 6);

 const handleShare = async () => {
  const shareData = {
   title: teamName,
   text: `View ${teamName} on SportingHip`,
   url: window.location.href,
  };

  try {
   if (navigator.share) {
    await navigator.share(shareData);
    return;
   }

   await navigator.clipboard.writeText(window.location.href);
  } catch (error) {
   console.error("Unable to share team:", error);
  }
 };

 const openGame = (gameId) => {
  if (!gameId) return;

  navigate(
   slug
    ? `/${slug}/games/${gameId}`
    : `/games/${gameId}`
  );
 };

 const divisionName =
  team?.division?.name ||
  team?.divisionName ||
  team?.division_name ||
  null;

 const leagueName =
  team?.league?.name ||
  team?.leagueName ||
  team?.league_name ||
  null;

 const location =
  team?.location ||
  team?.homeCourt ||
  team?.home_court ||
  null;

 const seasonName =
  team?.season?.name ||
  team?.seasonName ||
  team?.season_name ||
  null;
console.log(team)
 return (
  <div
   className={`racquet-team-public-page racquet-team-public-theme-${theme}`}
  >
   <main className="racquet-team-public-shell">
    <section className="racquet-team-public-hero">
     <div className="racquet-team-public-logo">
      {teamLogo ? (
       <img
        src={teamLogo}
        alt={`${teamName} logo`}
        className="racquet-team-public-logo-image"
       />
      ) : (
       <div className="racquet-team-public-logo-mark">
        <span>{getInitials(teamName)}</span>
       </div>
      )}
     </div>

     <div className="racquet-team-public-main">
      <div className="racquet-team-public-title-row">
       <div>
        <h1>{teamName}</h1>

        <div className="racquet-team-public-meta">
         {divisionName && <span>{divisionName}</span>}

         {divisionName && captainName !== "—" && (
          <span>•</span>
         )}

         {captainName !== "—" && (
          <span>Captain: {captainName}</span>
         )}

         {(divisionName || captainName !== "—") &&
          location && <span>•</span>}

         {location && <span>{location}</span>}
        </div>
       </div>

       {/* <div className="racquet-team-public-actions">
        <button
         type="button"
         className="racquet-team-public-share-button"
         onClick={handleShare}
        >
         <Share2 size={16} />
         Share Team 
        </button>
       </div> */}
      </div>

      <div className="racquet-team-public-stats">
       {teamStats.map((stat) => (
        <div key={stat.label}>
         <p>{stat.label}</p>
         <strong>{stat.value}</strong>
        </div>
       ))}
      </div>
     </div>

     <aside className="racquet-team-public-next-match-card">
      <p>Next Match</p>

      {nextGame ? (
       <>
        <h3>vs {getOpponentName(nextGame)}</h3>

        <span>
         {formatDate(nextGame.date)}
         {nextGame.time
          ? ` · ${formatTime(nextGame.time)}`
          : ""}
        </span>

        <small>
         {nextGame.location || "Location TBD"}
        </small>

        <div className="racquet-team-public-mini-matchup">
         <div>
          <div className="racquet-team-public-mini-badge racquet-team-public-mini-badge-home">
           {getInitials(teamName)}
          </div>

          <strong>{teamName}</strong>
         </div>

         <span>vs</span>

         <div>
          <div className="racquet-team-public-mini-badge racquet-team-public-mini-badge-away">
           {getInitials(getOpponentName(nextGame))}
          </div>

          <strong>{getOpponentName(nextGame)}</strong>
         </div>
        </div>

        <button
         type="button"
         className="racquet-team-public-view-match-button"
         onClick={() => openGame(nextGame.id)}
        >
         View Match
        </button>
       </>
      ) : (
       <>
        <h3>No upcoming match</h3>
        <span>Schedule has not been added yet.</span>
       </>
      )}
     </aside>
    </section>

    {/* <section
     className={`racquet-team-public-photo ${teamPhoto
       ? "racquet-team-public-photo-has-image"
       : ""
      }`}
     style={
      teamPhoto
       ? {
        backgroundImage: `linear-gradient(
                    90deg,
                    rgba(16, 32, 23, 0.9),
                    rgba(15, 81, 50, 0.45)
                  ), url("${teamPhoto}")`,
       }
       : undefined
     }
    >
     <div className="racquet-team-public-photo-content">
      <p>{teamName}</p>

      <h2>Team tennis, built for match day.</h2>

      <span>
       {team?.sport?.type || "Racquet Sports"}
      </span>
     </div>

     {!teamPhoto && (
      <div className="racquet-team-public-court-lines">
       <div />
       <div />
       <div />
      </div>
     )}
    </section> */}

    {bannerSlot && (
     <div className="racquet-team-public-banner">
      {bannerSlot}
     </div>
    )}

    <div className="racquet-team-public-tabs">
     <button type="button" className="active">
      Overview
     </button>


     {/* <button type="button">Results</button>
     <button type="button">Stats</button>
     <button type="button">Highlights</button> */}
    </div>

    <section className="racquet-team-public-grid">
     <div className="racquet-team-public-card racquet-team-public-roster-card">
      <div className="racquet-team-public-card-header">
       <h3>Roster ({roster.length})</h3>

       {roster.length > rosterPreview.length && (
        <button
         type="button"
         className="racquet-team-public-text-button"
        >
         View Full Roster
         <ChevronRight size={15} />
        </button>
       )}
      </div>

      <div className="racquet-team-public-table-wrap">
       <table>
        <thead>
         <tr>
          <th>Player</th>
          <th>Play</th>
          <th>Win %</th>
         </tr>
        </thead>

        <tbody>
         {rosterPreview.length > 0 ? (
          rosterPreview.map((player) => {
           const playerName = getPlayerName(player);

           return (
            <tr key={player.id}>
             <td>
              <NavLink
               className="racquet-team-public-player-link"
               to={
                slug
                 ? `/${slug}/players/${player.id}`
                 : `/players/${player.id}`
               }
              >
               <div className="racquet-team-public-player-avatar">
                {player?.image ? (
                 <img
                  src={player.image}
                  alt={playerName}
                 />
                ) : (
                 getInitials(playerName)
                )}
               </div>

               <span>{playerName}</span>
              </NavLink>
             </td>
            <td>
                 {player.position || "—"}-Handed
            </td>
             <td>
              {getPlayerWinPercentage(player)}
             </td>
            </tr>
           );
          })
         ) : (
          <tr>
           <td
            colSpan="4"
            className="racquet-team-public-empty-cell"
           >
            No players have been added yet.
           </td>
          </tr>
         )}
        </tbody>
       </table>
      </div>
     </div>

     <div className="racquet-team-public-card racquet-team-public-results-card">
      <div className="racquet-team-public-card-header">
       <h3>Team Results</h3>

       {completedGames.length > recentResults.length && (
        <button
         type="button"
         className="racquet-team-public-text-button"
        >
         View All Results
         <ChevronRight size={15} />
        </button>
       )}
      </div>

      <div className="racquet-team-public-table-wrap">
       <table>
        <thead>
         <tr>
          <th>Date</th>
          <th>Opponent</th>
          <th>Result</th>
          <th>Score</th>
         </tr>
        </thead>

        <tbody>
         {recentResults.length > 0 ? (
          recentResults.map((game) => {
           const result = getResultLabel(game);

           return (
            <tr
             key={game.id}
             className="racquet-team-public-game-row"
             onClick={() => openGame(game.id)}
            >
             <td>{formatDate(game.date)}</td>

             <td>{getOpponentName(game)}</td>

             <td>
              <span
               className={`racquet-team-public-result racquet-team-public-result-${result.toLowerCase()}`}
              >
               {result}
              </span>
             </td>

             <td>{formatScore(game)}</td>
            </tr>
           );
          })
         ) : (
          <tr>
           <td
            colSpan="4"
            className="racquet-team-public-empty-cell"
           >
            No completed matches available.
           </td>
          </tr>
         )}
        </tbody>
       </table>
      </div>
     </div>
    </section>

    {/* <section className="racquet-team-public-bottom-grid">
     <div className="racquet-team-public-card racquet-team-public-leaders-card">
      <div className="racquet-team-public-card-header">
       <h3>Team Leaders</h3>
      </div>

      <div className="racquet-team-public-leader-list">
       <div>
        <Trophy size={22} />

        <span>Top Singles Record</span>

        <strong>
         {team?.topSinglesLeader || "—"}
        </strong>
       </div>

       <div>
        <Users size={22} />

        <span>Top Doubles Pair</span>

        <strong>
         {team?.topDoublesPair || "—"}
        </strong>
       </div>

       <div>
        <Target size={22} />

        <span>Most Sets Won</span>

        <strong>
         {team?.mostSetsWonLeader || "—"}
        </strong>
       </div>
      </div>
     </div>

     <div className="racquet-team-public-card racquet-team-public-about-card">
      <div className="racquet-team-public-card-header">
       <h3>About {teamName}</h3>
      </div>

      {team?.description || team?.about ? (
       <p>{team.description || team.about}</p>
      ) : (
       <p className="racquet-team-public-muted-text">
        No team description has been added yet.
       </p>
      )}

      <div className="racquet-team-public-about-list">
       <div>
        <Shield size={17} />
        <span>Captain</span>
        <strong>{captainName}</strong>
       </div>

       <div>
        <CircleDot size={17} />
        <span>Division</span>
        <strong>{divisionName || "—"}</strong>
       </div>

       <div>
        <MapPin size={17} />
        <span>Home Court</span>
        <strong>{location || "—"}</strong>
       </div>

       <div>
        <CalendarDays size={17} />
        <span>Season</span>
        <strong>{seasonName || "—"}</strong>
       </div>
      </div>
     </div>
    </section> */}
   </main>
  </div>
 );
}

export default RacquetTeamPublic;